import React, { useCallback } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  ReferralPage,
  FindReferralsByUserIdAdminQueryVariables
} from "../../../../../../schemaTypes";

export const findReferralsByUserIdAdminQuery = gql`
  query FindReferralsByUserIdAdmin(
    $userId: String!
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findReferralsByUserIdAdmin(
      userId: $userId
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        customer {
          id
          firstname
          lastname
          username
          createdAt
          updatedAt
        }
      }
      referralCode
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindReferralsByUserIdAdmin {
  referrals: ReferralPage[];
  referralCode: string;
  loading: boolean;
  loadMore: () => void;
  hasMoreReferrals: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: FindReferralsByUserIdAdminQueryVariables;
  children: (data: WithFindReferralsByUserIdAdmin) => React.ReactElement | null;
}

export const FindReferralsByUserIdAdmin: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    { findReferralsByUserIdAdmin: ReferralPage },
    FindReferralsByUserIdAdminQueryVariables
  >
  (findReferralsByUserIdAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const referrals = data?.findReferralsByUserIdAdmin?.data || [];
  const referralCode = data?.findReferralsByUserIdAdmin?.referralCode || "";
  const page = data?.findReferralsByUserIdAdmin?.page || variables.page;
  const pageSize = data?.findReferralsByUserIdAdmin?.pageSize || variables.pageSize;
  const total = data?.findReferralsByUserIdAdmin?.total || 0;
  const totalPages = data?.findReferralsByUserIdAdmin?.totalPages || 0;
  const hasMoreReferrals = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreReferrals) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findReferralsByUserIdAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findReferralsByUserIdAdmin: {
            ...fetchMoreResult?.findReferralsByUserIdAdmin,
            data: [
              ...(prev?.findReferralsByUserIdAdmin?.data || []),
              ...(fetchMoreResult?.findReferralsByUserIdAdmin?.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreReferrals]);

  return children({
    referrals: referrals as any[],
    referralCode,
    loading,
    loadMore,
    hasMoreReferrals,
    page,
    pageSize,
    total,
    totalPages,
  });
};